/**
 * Textarea Primitive
 * Multi-line text input component with validation states and design system tokens
 */

import React from "react";
import {
  Textarea as ChakraTextarea,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
} from "@chakra-ui/react";
import {
  resolveSpacing,
} from "../foundation";
import { Color } from "../foundation/Color";
import { Spacing } from "../foundation/Spacing";
import { Radius } from "../foundation/Radius";
import { useColorModeValue } from "../hooks/useColorModeValue";

// Branded types for textarea tokens
declare const __textarea_size_brand: unique symbol;
declare const __textarea_state_brand: unique symbol;

export interface TextareaSizeToken {
  readonly [__textarea_size_brand]: true;
  readonly _value: string;
}

export interface TextareaStateToken {
  readonly [__textarea_state_brand]: true;
  readonly _value: string;
}

// Factory functions
const createTextareaSizeToken = (value: string): TextareaSizeToken => ({ _value: value } as TextareaSizeToken);
const createTextareaStateToken = (value: string): TextareaStateToken => ({ _value: value } as TextareaStateToken);

export interface TextareaProps {
  label?: string;
  helperText?: string;
  errorText?: string;
  state?: TextareaStateToken;
  size?: TextareaSizeToken;
  fullWidth?: boolean;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  rows?: number;
  cols?: number;
  minRows?: number;
  maxRows?: number;
  resize?: "none" | "both" | "horizontal" | "vertical";
  name?: string;
  id?: string;
  "data-testid"?: string;
}

function TextareaComponent({
  label,
  helperText,
  errorText,
  state,
  size,
  fullWidth = false,
  placeholder,
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  readOnly = false,
  required = false,
  rows = 4,
  cols,
  minRows,
  maxRows,
  resize = "vertical",
  name,
  id,
  "data-testid": testId,
}: TextareaProps) {
  // Set defaults inside the component to avoid circular reference issues
  const resolvedState = state || textareaTokens.state.default;
  const resolvedSize = size || textareaTokens.size.medium;

  // Determine validation state
  const isInvalid = resolvedState._value === "error" || !!errorText;
  const isSuccess = resolvedState._value === "success";
  const isWarning = resolvedState._value === "warning";

  // Get state colors
  const getStateColors = () => {
    if (isInvalid) {
      return {
        borderColor: useColorModeValue(Color.primitive.red(500)),
        focusBorderColor: useColorModeValue(Color.primitive.red(500)),
        _focus: {
          borderColor: useColorModeValue(Color.primitive.red(500)),
          boxShadow: `0 0 0 1px ${useColorModeValue(Color.primitive.red(500))}`,
        },
      };
    } else if (isSuccess) {
      return {
        borderColor: useColorModeValue(Color.primitive.green(500)),
        focusBorderColor: useColorModeValue(Color.primitive.green(500)),
        _focus: {
          borderColor: useColorModeValue(Color.primitive.green(500)),
          boxShadow: `0 0 0 1px ${useColorModeValue(Color.primitive.green(500))}`,
        },
      };
    } else if (isWarning) {
      return {
        borderColor: useColorModeValue(Color.primitive.orange(500)),
        focusBorderColor: useColorModeValue(Color.primitive.orange(500)),
        _focus: {
          borderColor: useColorModeValue(Color.primitive.orange(500)),
          boxShadow: `0 0 0 1px ${useColorModeValue(Color.primitive.orange(500))}`,
        },
      };
    }

    return {
      borderColor: useColorModeValue(Color.border.primary),
      focusBorderColor: useColorModeValue(Color.interactive.primary),
      _focus: {
        borderColor: useColorModeValue(Color.interactive.primary),
        boxShadow: `0 0 0 1px ${useColorModeValue(Color.interactive.primary)}`,
      },
    };
  };

  const getSizeProps = (size: TextareaSizeToken) => {
    switch (size._value) {
      case "small":
        return {
          size: "sm" as const,
          fontSize: "sm",
          px: resolveSpacing(Spacing.small),
          py: resolveSpacing(Spacing.tiny),
        };
      case "large":
        return {
          size: "lg" as const,
          fontSize: "md",
          px: resolveSpacing(Spacing.medium),
          py: resolveSpacing(Spacing.small),
        };
      case "medium":
      default:
        return {
          size: "md" as const,
          fontSize: "sm",
          px: resolveSpacing(Spacing.small),
          py: resolveSpacing(Spacing.tiny),
        };
    }
  };

  const stateColors = getStateColors();
  const sizeProps = getSizeProps(resolvedSize);

  return (
    <FormControl isInvalid={isInvalid} isRequired={required} width={fullWidth ? "full" : "auto"}>
      {label && (
        <FormLabel
          htmlFor={id}
          fontSize={sizeProps.fontSize}
          color={useColorModeValue(Color.text.primary)}
          mb={resolveSpacing(Spacing.tiny)}
        >
          {label}
        </FormLabel>
      )}

      <ChakraTextarea
        {...sizeProps}
        {...stateColors}
        bg={useColorModeValue(Color.surface.input)}
        color={useColorModeValue(Color.text.primary)}
        placeholder={placeholder}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        isDisabled={disabled}
        isReadOnly={readOnly}
        isRequired={required}
        rows={rows}
        cols={cols}
        minRows={minRows}
        maxRows={maxRows}
        resize={resize}
        name={name}
        id={id}
        data-testid={testId}
        borderRadius={Radius.input._value}
        width={fullWidth ? "full" : "auto"}
        _placeholder={{
          color: useColorModeValue(Color.text.placeholder),
        }}
        _disabled={{
          opacity: 0.6,
          cursor: "not-allowed",
          bg: useColorModeValue(Color.surface.secondary),
        }}
      />

      {errorText && (
        <FormErrorMessage
          fontSize="xs"
          color={useColorModeValue(Color.text.error)}
          mt={resolveSpacing(Spacing.tiny)}
        >
          {errorText}
        </FormErrorMessage>
      )}

      {helperText && !errorText && (
        <FormHelperText
          fontSize="xs"
          color={useColorModeValue(Color.text.secondary)}
          mt={resolveSpacing(Spacing.tiny)}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

// Textarea tokens
const textareaTokens = {
  size: {
    small: createTextareaSizeToken("small"),
    medium: createTextareaSizeToken("medium"),
    large: createTextareaSizeToken("large"),
  } as const,

  state: {
    default: createTextareaStateToken("default"),
    success: createTextareaStateToken("success"),
    warning: createTextareaStateToken("warning"),
    error: createTextareaStateToken("error"),
  } as const,
};

// Public API - combines tokens and component
export const Textarea = Object.assign(TextareaComponent, textareaTokens);
